import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, LayerGroup, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import '../styles/index.css';

// 👇 IMPORTAÇÃO DOS ALERTAS PADRONIZADOS E MODAL
import { askConfirm } from '../utils/alertas';
import TrocaSenhaModal from '../components/TrocaSenhaModal';
import TelemetriaModal from '../components/TelemetriaModal';
import ConfiguracoesMapaModal from '../components/ConfiguracoesMapaModal';

// --- 🛠️ CONFIGURAÇÕES DE AMBIENTE ---
const IS_PROD = true;

const API_BASE_URL = IS_PROD
    ? 'https://sigoapi.jhe-etes.com.br/api'
    : 'http://localhost:3001/api';

const COLOR_DEFAULT = '#64748b'; // Cinza padrão

// --- 1. FUNÇÃO ÍCONE (PIN) DINÂMICO ---
const getPinIcon = (color) => {
    const pinColor = color || COLOR_DEFAULT;
    const svgTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="28" height="30">
      <filter id="shadow"><feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="#00000033"/></filter>
      <path fill="${pinColor}" stroke="#ffffff" stroke-width="10" filter="url(#shadow)" d="M384 192c0 87.4-117 243-168.3 307.2c-12.3 15.3-35.1 15.3-47.4 0C117 435 0 279.4 0 192C0 86 86 0 192 0s192 86 192 192z"/>
      <circle cx="192" cy="192" r="64" fill="#ffffff"/>
    </svg>
  `;
    return L.divIcon({
        className: 'custom-svg-pin',
        html: svgTemplate,
        iconSize: [32, 42],
        iconAnchor: [16, 42],
        popupAnchor: [0, -42]
    });
};

// --- 2. COMPONENTE DE SELECT PERSONALIZADO ---
const CustomSelect = ({ label, value, onChange, options, defaultText }) => (
    <div className="mb-4">
        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">{label}</label>
        <div className="relative">
            <select
                value={value}
                onChange={onChange}
                className="w-full p-3 pl-4 pr-10 bg-white border-2 border-gray-100 text-gray-700 text-sm rounded-xl appearance-none focus:outline-none focus:border-sabesp-cyan/50 focus:ring-2 focus:ring-sabesp-cyan/20 transition-all cursor-pointer shadow-sm hover:border-gray-200"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: `right 0.75rem center`,
                    backgroundRepeat: `no-repeat`,
                    backgroundSize: `1.5em 1.5em`
                }}
            >
                <option value="Todos">{defaultText}</option>
                {options.map((opt, idx) => (
                    <option key={idx} value={opt.nome || opt}>
                        {opt.label || opt.nome || opt}
                    </option>
                ))}
            </select>
        </div>
    </div>
);

// --- 3. COMPONENTE MULTI-SELECT DE ETES COMPACTO RETRÁTIL ---
const EtesMultiSelect = ({ options, selectedEtes, setSelectedEtes }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Fechar ao clicar fora do componente
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filtrar ETEs disponíveis com base no termo digitado
    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) return options;
        return options.filter(emp =>
            emp.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.nome_municipio && emp.nome_municipio.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [options, searchTerm]);

    const isAllSelected = options.length > 0 && selectedEtes.length === options.length;

    const handleLimparComBlindagem = (e) => {
        if (e) {
            e.stopPropagation();
            if (e.preventDefault && typeof e.preventDefault === 'function') {
                e.preventDefault();
            }
        }
        setSelectedEtes([]);
    };

    const toggleSelectAll = (e) => {
        if (e) {
            e.stopPropagation();
            if (e.preventDefault && typeof e.preventDefault === 'function') {
                e.preventDefault();
            }
        }
        if (isAllSelected) {
            setSelectedEtes([]);
        } else {
            setSelectedEtes(options.map(emp => emp.id));
        }
    };

    const toggleOption = (id, e) => {
        if (e) {
            e.stopPropagation();
        }
        if (selectedEtes.includes(id)) {
            setSelectedEtes(selectedEtes.filter(item => item !== id));
        } else {
            setSelectedEtes([...selectedEtes, id]);
        }
    };

    // Texto/Resumo Inteligente para o botão compacto
    const renderSummaryLabel = () => {
        if (selectedEtes.length === 0) {
            return <span className="text-gray-500 font-medium">Todas as ETEs</span>;
        }
        if (isAllSelected) {
            return <span className="text-sabesp-blue font-bold">Todas as ETEs ({options.length})</span>;
        }
        if (selectedEtes.length === 1) {
            const selectedItem = options.find(o => o.id === selectedEtes[0]);
            return <span className="text-sabesp-blue font-bold truncate max-w-[160px]">{selectedItem ? selectedItem.nome : '1 ETE Selecionada'}</span>;
        }
        return (
            <span className="inline-flex items-center gap-1.5 bg-blue-50 text-sabesp-blue font-bold px-2.5 py-0.5 rounded-lg border border-blue-100 text-xs">
                <span>🚰</span> {selectedEtes.length} ETEs Selecionadas
            </span>
        );
    };

    return (
        <div className="mb-6 relative" ref={dropdownRef}>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">
                FILTRAR POR ETES (SELEÇÃO MÚLTIPLA)
            </label>

            {/* Trigger Compacto de 1 Linha (Altura Fixa h-11 / 44px) */}
            <div
                onClick={() => setIsOpen(prev => !prev)}
                className="w-full h-11 px-3.5 bg-white border-2 border-gray-100 rounded-xl cursor-pointer hover:border-gray-200 focus-within:border-sabesp-cyan/50 focus-within:ring-2 focus-within:ring-sabesp-cyan/20 transition-all shadow-sm flex items-center justify-between gap-2"
            >
                <div className="flex items-center gap-2 truncate text-sm">
                    {renderSummaryLabel()}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {selectedEtes.length > 0 && (
                        <button
                            type="button"
                            onClick={handleLimparComBlindagem}
                            onTouchEnd={handleLimparComBlindagem}
                            className="text-xs font-bold text-gray-400 hover:text-red-500 bg-gray-100 hover:bg-red-50 rounded-full w-5 h-5 flex items-center justify-center transition-colors"
                            title="Limpar seleção de ETEs"
                        >
                            ✕
                        </button>
                    )}
                    <span className="text-gray-400 text-xs pointer-events-none">
                        {isOpen ? '▲' : '▼'}
                    </span>
                </div>
            </div>

            {/* Popover / Menu Dropdown Suspenso com Z-Index e Barra Interna */}
            {isOpen && (
                <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden p-2.5 animate-fadeIn">

                    {/* Ações Rápidas no Cabeçalho do Popover */}
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100 px-1 mb-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            {selectedEtes.length} de {options.length} selecionadas
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={toggleSelectAll}
                                onTouchEnd={toggleSelectAll}
                                className="text-[10px] font-bold text-sabesp-cyan hover:underline uppercase"
                            >
                                {isAllSelected ? 'Desmarcar Todas' : 'Selecionar Todas'}
                            </button>
                            {selectedEtes.length > 0 && !isAllSelected && (
                                <button
                                    type="button"
                                    onClick={handleLimparComBlindagem}
                                    onTouchEnd={handleLimparComBlindagem}
                                    className="text-[10px] font-bold text-red-500 hover:underline uppercase"
                                >
                                    Limpar
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Input de Pesquisa Interno */}
                    <div className="relative mb-2">
                        <input
                            type="text"
                            placeholder="Buscar ETE pelo nome ou cidade..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            className="w-full p-2.5 pl-8 bg-slate-50 border border-gray-200 rounded-lg text-xs font-medium focus:bg-white focus:border-sabesp-cyan outline-none transition-all"
                        />
                        <span className="absolute left-2.5 top-2.5 text-gray-400 text-xs pointer-events-none">🔍</span>
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 text-xs"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Lista com Rolagem Interna Limpa (max-h-48 no mobile / max-h-52 na desktop) */}
                    <div className="max-h-48 md:max-h-52 overflow-y-auto custom-scrollbar space-y-0.5 pr-0.5">
                        {filteredOptions.length === 0 ? (
                            <div className="p-4 text-xs text-gray-400 text-center font-medium">
                                Nenhuma ETE encontrada para "{searchTerm}"
                            </div>
                        ) : (
                            filteredOptions.map((emp) => {
                                const isSelected = selectedEtes.includes(emp.id);
                                return (
                                    <div
                                        key={emp.id}
                                        onClick={() => toggleOption(emp.id)}
                                        className={`flex items-center justify-between p-2.5 md:p-2 rounded-lg text-xs cursor-pointer transition-colors ${isSelected
                                            ? 'bg-blue-50/80 text-sabesp-blue font-bold'
                                            : 'hover:bg-gray-50 text-gray-700 font-medium'
                                            }`}
                                    >
                                        <div className="flex flex-col pr-2 truncate">
                                            <span className="truncate text-xs">{emp.nome}</span>
                                            {emp.nome_municipio && (
                                                <span className="text-[10px] text-gray-400 font-normal truncate">
                                                    📍 {emp.nome_municipio}
                                                </span>
                                            )}
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => { }} // Gerido pelo click do container
                                            className="rounded border-gray-300 text-sabesp-cyan focus:ring-sabesp-cyan h-4 w-4 shrink-0"
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

function ResetMap({ center }) {
    const map = useMap();
    useEffect(() => { map.setView(center, 10); }, [center, map]);
    return null;
}

function Mapa() {
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSenhaModalOpen, setIsSenhaModalOpen] = useState(false);
    const [isTelemetriaModalOpen, setIsTelemetriaModalOpen] = useState(false);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

    // 🎛️ ESTADO DE CONFIGURAÇÃO DE EXIBIÇÃO DA SIDEBAR (SWITCHES)
    const [sidebarConfig, setSidebarConfig] = useState({
        showPesquisa: true,
        showMultiSelect: true,
        showGerencia: true,
        showMunicipio: true,
        showTipoMapa: true,
        showLimparFiltros: true
    });

    // 🔐 Auth 
    const token = localStorage.getItem('token');
    const isLoggedIn = !!token;

    // Dados
    const [empreendimentos, setEmpreendimentos] = useState([]);
    const [listaGerencias, setListaGerencias] = useState([]);

    // Filtros
    const [busca, setBusca] = useState('');
    const [filtroMunicipio, setFiltroMunicipio] = useState('Todos');
    const [filtroGerencia, setFiltroGerencia] = useState('Todos');
    const [selectedEtes, setSelectedEtes] = useState([]); // Array de IDs de ETEs selecionadas
    const [mapType, setMapType] = useState('padrao'); // 'padrao' ou 'satelite'
    const [gerenciasOcultas, setGerenciasOcultas] = useState([]); // Nomes das gerências ocultadas no mapa

    // 🔥 LÓGICA: MUNICÍPIOS DINÂMICOS
    // 🔥 LÓGICA: MUNICÍPIOS DINÂMICOS (Todos os municípios disponíveis no sistema)
    const municipiosComObras = useMemo(() => {
        const nomes = empreendimentos
            .map(emp => emp.nome_municipio)
            .filter(nome => nome && nome.trim() !== "");
        return [...new Set(nomes)].sort();
    }, [empreendimentos]);

    // 🔥 LÓGICA DE FILTRAGEM UNIFICADA E SINCRONIZADA
    const empreendimentosExibidos = useMemo(() => {
        const term = busca.trim().toLowerCase();
        return empreendimentos.filter(emp => {
            // Filtro por Visibilidade da Gerência (Ocultada no modal de configurações)
            if (emp.nome_gerencia && gerenciasOcultas.includes(emp.nome_gerencia)) {
                return false;
            }
            // Filtro por ETEs selecionadas no multi-select (se houver seleção específica)
            if (selectedEtes.length > 0 && !selectedEtes.includes(emp.id)) {
                return false;
            }
            // Filtro por Gerência selecionada na sidebar
            if (filtroGerencia !== 'Todos' && emp.nome_gerencia !== filtroGerencia) {
                return false;
            }
            // Filtro por Município
            if (filtroMunicipio !== 'Todos' && emp.nome_municipio !== filtroMunicipio) {
                return false;
            }
            // Filtro por Busca Rápida (Nome, Contrato, Município, SAP)
            if (term) {
                const matchNome = emp.nome && emp.nome.toLowerCase().includes(term);
                const matchMuni = emp.nome_municipio && emp.nome_municipio.toLowerCase().includes(term);
                const matchGer = emp.nome_gerencia && emp.nome_gerencia.toLowerCase().includes(term);
                const matchSAP = emp.codigo_sap && emp.codigo_sap.toLowerCase().includes(term);
                if (!matchNome && !matchMuni && !matchGer && !matchSAP) {
                    return false;
                }
            }
            return true;
        });
    }, [empreendimentos, selectedEtes, filtroGerencia, filtroMunicipio, gerenciasOcultas, busca]);

    // Filtrar a lista de gerências no dropdown da sidebar excluindo as gerências ocultadas
    const listaGerenciasVisiveis = useMemo(() => {
        if (gerenciasOcultas.length === 0) return listaGerencias;
        return listaGerencias.filter(g => !gerenciasOcultas.includes(g.nome));
    }, [listaGerencias, gerenciasOcultas]);

    const handleAuthAction = async () => {
        if (isLoggedIn) {
            const confirmed = await askConfirm("Atenção", "Deseja realmente sair do sistema?");
            if (!confirmed) return;

            try {
                await fetch(`${API_BASE_URL}/logout`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            } catch (error) { console.error("Erro logout:", error); }

            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.reload();
        } else {
            navigate('/login');
        }
    };

    useEffect(() => {
        async function carregarListas() {
            try {
                const respGer = await fetch(`${API_BASE_URL}/gerencias`);
                if (respGer.ok) {
                    const gerRaw = await respGer.json();
                    const gerFormatada = gerRaw.map(g => ({
                        ...g,
                        label: `${g.nome} – ${g.responsavel || 'Sem Responsável'}`
                    }));
                    setListaGerencias(gerFormatada);
                }
            } catch (error) { console.error("Erro ao carregar listas:", error); }
        }
        carregarListas();
    }, []);

    useEffect(() => {
        const fetchDados = async () => {
            const cacheKey = `sigo_cache_all_empreendimentos`;
            const cachedData = sessionStorage.getItem(cacheKey);
            if (cachedData) setEmpreendimentos(JSON.parse(cachedData));

            try {
                const headers = { 'Content-Type': 'application/json' };
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const response = await fetch(`${API_BASE_URL}/empreendimentos`, {
                    method: 'GET',
                    headers: headers
                });

                if (response.status === 403 && token) {
                    console.warn("⚠️ Token expirado ou inválido. Removendo do localStorage e acessando de forma pública.");
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.reload();
                    return;
                }

                if (response.ok) {
                    const data = await response.json();
                    if (JSON.stringify(data) !== cachedData) {
                        setEmpreendimentos(data);
                        sessionStorage.setItem(cacheKey, JSON.stringify(data));
                    }
                }
            } catch (error) { console.error("Erro na busca:", error); }
        };

        fetchDados();
    }, [token]);

    const limparFiltros = () => {
        setFiltroMunicipio('Todos');
        setFiltroGerencia('Todos');
        setBusca('');
        setSelectedEtes([]);
    };

    return (
        <div className="flex h-screen w-full font-sans bg-slate-50 overflow-hidden relative">
            {isSidebarOpen && (<div className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm touch-none" onClick={() => setIsSidebarOpen(false)}></div>)}

            <aside className={`fixed inset-y-0 left-0 z-40 w-80 max-w-[85vw] md:max-w-none h-full flex flex-col shadow-2xl bg-white/95 backdrop-blur-xl border-r border-white/20 transform transition-transform duration-300 md:relative md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="p-6 pb-4 flex justify-between items-center shrink-0">
                    <div>
                        <div className="flex items-center gap-2 mb-1"><span className="text-2xl">💧</span><h1 className="text-2xl font-extrabold text-sabesp-blue tracking-tight">SIGO Sabesp</h1></div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest ml-9">Painel de Obras</p>
                    </div>
                    <button onClick={() => setIsSidebarOpen(false)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-700 text-xl font-bold rounded-full hover:bg-gray-100 transition-colors">✕</button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {sidebarConfig.showPesquisa && (
                        <div className="mb-6">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">Pesquisa Rápida</label>
                            <div className="relative">
                                <input type="text" placeholder="Nome, contrato ou cidade..." value={busca} onChange={(e) => setBusca(e.target.value)} className="w-full p-3 pl-10 bg-white border-2 border-blue-50 rounded-xl text-sm focus:outline-none focus:border-sabesp-cyan transition-all shadow-sm" />
                                <span className="absolute left-3 top-3 text-gray-300 text-lg">🔍</span>
                            </div>
                        </div>
                    )}

                    {/* 🚰 MULTI-SELECT PESQUISÁVEL DE ETES */}
                    {sidebarConfig.showMultiSelect && (
                        <EtesMultiSelect
                            options={empreendimentos}
                            selectedEtes={selectedEtes}
                            setSelectedEtes={setSelectedEtes}
                        />
                    )}

                    <div className="space-y-2 mb-6">
                        {sidebarConfig.showGerencia && (
                            <CustomSelect label="Gerência" value={filtroGerencia} onChange={(e) => setFiltroGerencia(e.target.value)} options={listaGerenciasVisiveis} defaultText="Todas as gerências" />
                        )}
                        {sidebarConfig.showMunicipio && (
                            <CustomSelect label="Município" value={filtroMunicipio} onChange={(e) => setFiltroMunicipio(e.target.value)} options={municipiosComObras} defaultText="Todos os municípios" />
                        )}
                    </div>

                    {/* 🗺️ SELEÇÃO DE TIPO DE MAPA */}
                    {sidebarConfig.showTipoMapa && (
                        <div className="mb-8 bg-slate-50 p-3 rounded-2xl border border-gray-100 shadow-inner">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5 ml-1">
                                TIPO DE VISUALIZAÇÃO DO MAPA
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setMapType('padrao')}
                                    className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-bold transition-all border ${mapType === 'padrao'
                                        ? 'bg-white text-sabesp-blue border-[#00AEEF] shadow-sm ring-2 ring-[#00AEEF]/20'
                                        : 'bg-transparent text-gray-500 border-transparent hover:bg-gray-200/50 hover:text-gray-700'
                                        }`}
                                >
                                    <span>🗺️</span> Mapa Padrão
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMapType('satelite')}
                                    className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-bold transition-all border ${mapType === 'satelite'
                                        ? 'bg-white text-sabesp-blue border-[#00AEEF] shadow-sm ring-2 ring-[#00AEEF]/20'
                                        : 'bg-transparent text-gray-500 border-transparent hover:bg-gray-200/50 hover:text-gray-700'
                                        }`}
                                >
                                    <span>🛰️</span> Satélite
                                </button>
                            </div>
                        </div>
                    )}

                    {sidebarConfig.showLimparFiltros && (
                        <button onClick={limparFiltros} className="w-full py-3 bg-blue-50 text-sabesp-blue hover:bg-sabesp-blue hover:text-white rounded-xl text-sm font-bold transition-all duration-300 shadow-sm flex items-center justify-center gap-2 mb-8 group">
                            <span className="group-hover:rotate-12 transition-transform">🧹</span> Limpar Filtros
                        </button>
                    )}
                </div>

                {/* 👇 RODAPÉ ORIGINAL DISCRETO PRESERVADO */}
                <div className="p-4 bg-gray-50 border-t border-gray-100 text-center flex flex-col gap-3">
                    <p className="text-[11px] font-bold text-gray-400 uppercase">Exibindo <span className="text-sabesp-blue text-sm">{empreendimentosExibidos.length}</span> ETES</p>



                    {isLoggedIn && (
                        <>
                            <button
                                onClick={() => setIsConfigModalOpen(true)}
                                className="text-[10px] font-bold text-gray-500 hover:text-sabesp-blue uppercase tracking-widest py-1 px-3 border border-transparent hover:border-blue-100 rounded-full transition-all flex items-center justify-center gap-1 mx-auto"
                            >
                                <span>⚙️</span> CONFIGURAÇÕES
                            </button>
                            <button
                                onClick={() => setIsSenhaModalOpen(true)}
                                className="text-[10px] font-bold text-gray-500 hover:text-sabesp-blue uppercase tracking-widest py-1 px-3 border border-transparent hover:border-blue-100 rounded-full transition-all flex items-center justify-center gap-1 mx-auto"
                            >
                                🔒 Alterar Senha
                            </button>
                            <button
                                onClick={() => setIsTelemetriaModalOpen(true)}
                                className="text-[10px] font-bold text-sabesp-cyan hover:text-cyan-700 uppercase tracking-widest py-1 px-3 border border-cyan-100 hover:border-sabesp-cyan rounded-full transition-all flex items-center justify-center gap-1 mx-auto bg-cyan-50/50"
                            >
                                📊 PAINEL DE TELEMETRIA
                            </button>
                        </>
                    )}

                    {/*  * {isLoggedIn && (
                <button 
                    onClick={() => navigate('/admin')}
                    className="text-[10px] font-bold text-sabesp-blue hover:text-white hover:bg-sabesp-blue uppercase tracking-widest py-1.5 px-4 border border-blue-100 hover:border-sabesp-blue rounded-full transition-all flex items-center justify-center gap-1.5 mx-auto"
                >
                    ⚙️ Painel Admin
                </button>
            )}*/}

                    <button onClick={handleAuthAction} className={`text-[10px] font-bold uppercase tracking-widest py-1 px-3 rounded-full transition-all w-fit mx-auto ${isLoggedIn ? 'text-red-400 hover:bg-red-50' : 'text-gray-300 hover:text-sabesp-blue hover:bg-blue-50'}`}>
                        {isLoggedIn ? '🔓 Sair do Sistema' : '🔒 Acesso Restrito'}
                    </button>
                </div>
            </aside>

            <main className="flex-grow relative z-0 w-full h-full">
                <button onClick={() => setIsSidebarOpen(true)} className="md:hidden absolute top-4 left-4 z-[1000] bg-white text-sabesp-blue px-4 py-2.5 rounded-xl shadow-xl font-bold flex items-center gap-2 text-sm">☰ Filtros</button>

                <MapContainer center={[-23.55052, -46.63330]} zoom={10} style={{ height: "100%", width: "100%" }} zoomControl={false}>
                    {mapType === 'padrao' ? (
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                    ) : (
                        <TileLayer
                            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        />
                    )}
                    <ResetMap center={[-23.55052, -46.63330]} />

                    {/* 🛡️ LAYERGROUP COM KEY DINÂMICA PARA PREVENIR ERRO REMOVECHILD E RE-DESMONTAGEM LIMPA DO LEAFLET */}
                    <LayerGroup key={`layer-group-${selectedEtes.join('-')}-${empreendimentosExibidos.length}`}>
                        {empreendimentosExibidos.map((emp) => {
                            const corFinal = emp.cor || COLOR_DEFAULT;
                            const dynamicIcon = getPinIcon(corFinal);

                            return (
                                <Marker key={emp.id} position={emp.coordenadas} icon={dynamicIcon}>
                                    <Tooltip direction="top" offset={[0, -40]} opacity={1}><span className="font-bold text-xs">{emp.nome}</span></Tooltip>

                                    <Popup minWidth={280} maxWidth={320} className="custom-popup">
                                        <div className="p-1 font-sans">
                                            <div className="border-b border-gray-100 pb-2 mb-2">
                                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">{emp.nome_gerencia}</span>
                                                <h3 className="font-black text-sabesp-blue text-base uppercase leading-tight">{emp.nome}</h3>
                                                <p className="text-xs text-gray-500 font-medium mt-1">📍 {emp.nome_municipio}</p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 mb-4 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                <div className="text-center border-r border-gray-200 pr-1">
                                                    <span className="text-[9px] text-gray-400 uppercase font-bold block">Vazão Atual</span>
                                                    <span className="text-sm font-bold text-gray-700">{emp.vazao_atual_ls || '-'} L/s</span>
                                                </div>
                                                <div className="text-center">
                                                    <span className="text-[9px] text-gray-400 uppercase font-bold block">Vazão Futura</span>
                                                    <span className="font-bold text-sabesp-blue text-sm">{emp.vazao_futura_ls || '-'} L/s</span>
                                                </div>
                                            </div>

                                            {/* 👇 BLOCO DE CONTRATOS PRESERVADO INTEGRALMENTE */}
                                            {emp.contratos && emp.contratos.length > 0 && (
                                                <div className="mb-4 border-t border-gray-100 pt-3">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Contratos Vinculados</span>
                                                    <div className="space-y-2 max-h-32 overflow-y-auto custom-scrollbar pr-1">
                                                        {emp.contratos.map((c, idx) => (
                                                            <div key={idx} className="flex justify-between items-start text-[11px] bg-gray-50 p-2 rounded border border-gray-100">
                                                                <div>
                                                                    <span className="font-bold text-gray-800 block">{c.codigo}</span>
                                                                    <span className="text-[10px] text-gray-500 leading-tight block">{c.nome}</span>
                                                                </div>
                                                                <span
                                                                    className="text-[9px] font-bold text-white px-1.5 py-0.5 rounded uppercase tracking-wider ml-2 whitespace-nowrap"
                                                                    style={{ backgroundColor: c.cor || '#ccc' }}
                                                                >
                                                                    {c.status}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <button onClick={() => navigate(`/empreendimento/${emp.id}`)} className="w-full py-2.5 bg-sabesp-cyan text-white text-xs font-bold rounded-lg hover:bg-cyan-600 transition-all shadow-md uppercase tracking-wider">Ver Detalhes Completos</button>
                                        </div>
                                    </Popup>
                                </Marker>
                            )
                        })}
                    </LayerGroup>
                </MapContainer >

                {/* 🟢 BADGE DE AUDITORIA MINIMALISTA DISCRETO (CANTO INFERIOR ESQUERDO) */}
                <div className="absolute bottom-3 left-3 z-[400] flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/75 backdrop-blur-sm border border-slate-200/60 text-[11px] text-slate-500 font-normal shadow-sm select-none pointer-events-none">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span>Atualizado em <strong className="font-semibold text-slate-700">07/2026</strong></span>
                </div>
            </main >

            <TrocaSenhaModal
                isOpen={isSenhaModalOpen}
                onClose={() => setIsSenhaModalOpen(false)}
            />

            <TelemetriaModal
                isOpen={isTelemetriaModalOpen}
                onClose={() => setIsTelemetriaModalOpen(false)}
            />

            <ConfiguracoesMapaModal
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                sidebarConfig={sidebarConfig}
                setSidebarConfig={setSidebarConfig}
                gerenciasOcultas={gerenciasOcultas}
                setGerenciasOcultas={setGerenciasOcultas}
                onGerenciasUpdated={() => {
                    // Recarrega lista de gerências atualizada ao fechar ou salvar
                    fetch(`${API_BASE_URL}/gerencias`)
                        .then(res => res.json())
                        .then(gerRaw => {
                            const gerFormatada = gerRaw.map(g => ({
                                ...g,
                                label: `${g.nome} – ${g.responsavel || 'Sem Responsável'}`
                            }));
                            setListaGerencias(gerFormatada);
                        })
                        .catch(() => { });
                }}
            />
        </div>
    );
}

// Estilos mantidos originais
const styles = `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; } .leaflet-popup-content-wrapper { border-radius: 16px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1); } .leaflet-popup-content { margin: 12px; }`;
const styleTag = document.createElement("style");
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);

export default Mapa;
